import { useMutation } from "@tanstack/react-query";
import { Ban, MinusCircle } from "lucide-react";
import React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { blockUser } from "@/lib/apis/auth";
import { showError, showSuccess } from "@/lib/toast";

type BlockProfileModalProps = {
    showBlockDialog: boolean;
    setShowBlockDialog: React.Dispatch<React.SetStateAction<boolean>>;
    user: {
        avatar?: string;
        fullName: string;
        username: string;
        id: number;
    };
};

function BlockProfileModal({ showBlockDialog, setShowBlockDialog, user }: BlockProfileModalProps) {
    const { mutate, isPending } = useMutation({
        mutationFn: blockUser,
        onSuccess: () => {
            showSuccess(`From now block feature apply for ${user.fullName}`);
            window.location.reload();
        },
        onError: (error) => {
            showError(error.message);
        },
    });

    const handleBlockUser = () => {
        mutate(user.id);
    };

    return (
        <>
            <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Block User</DialogTitle>
                        <DialogDescription>
                            Blocking will prevent this user from sending you messages, viewing your profile, or interacting with your content.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-center space-x-4 py-4">
                        <Avatar className="h-12 w-12">
                            <AvatarImage src={user?.avatar} />
                            <AvatarFallback>{user?.fullName?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                            <h3 className="font-semibold">{user?.fullName}</h3>
                            <p className="text-sm text-muted-foreground">
                                @
                                {user?.username}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="font-medium">What happens when you block someone?</h3>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                            <li className="flex items-start">
                                <MinusCircle className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                                <span>They can no longer send you messages</span>
                            </li>
                            <li className="flex items-start">
                                <MinusCircle className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                                <span>They won't be able to see your posts or stories</span>
                            </li>
                            <li className="flex items-start">
                                <MinusCircle className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                                <span>You won't receive notifications from them</span>
                            </li>
                            <li className="flex items-start">
                                <MinusCircle className="h-4 w-4 mr-2 mt-0.5 text-muted-foreground" />
                                <span>They won't be notified that you've blocked them</span>
                            </li>
                        </ul>
                    </div>

                    <DialogFooter className="flex flex-col-reverse sm:flex-col sm:justify-end sm:space-x-2 gap-2">
                        <Button disabled={isPending} variant="outline" onClick={() => setShowBlockDialog(false)}>
                            Cancel
                        </Button>
                        <Button disabled={isPending} variant="destructive" onClick={handleBlockUser}>
                            <Ban className="h-4 w-4 mr-2" />
                            {isPending ? "Blocking User..." : "Block User"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default BlockProfileModal;
